/**
 * Sumbu yang bisa dizum, dihitung dalam ruang nilai.
 *
 * Sebuah gambar bisa diperbesar dengan dua cara, dan keduanya terlihat sama di
 * layar sampai orang membaca tepinya. Yang pertama memperbesar *gambarnya* —
 * satu transformasi pada kotaknya, seperti yang dilakukan peta — dan angka di
 * tepi sumbu ikut membesar bersama titik-titiknya, atau tetap diam sambil
 * berbohong. Yang kedua memperbesar *jendela nilainya*: yang berubah adalah
 * dari berapa sampai berapa sumbunya membentang, dan tandanya dihitung ulang
 * dari jendela itu. Hanya yang kedua yang memulangkan sumbu yang masih bisa
 * dibaca sesudah diperbesar dua puluh kali — dan sebuah crossplot yang tidak
 * bisa dibaca tepinya adalah awan berwarna, bukan pengukuran.
 *
 * Jendela disimpan sebagai pecahan 0..1 dari sumbu penuhnya dan bukan sebagai
 * nilai. Dengan begitu "kembali pas" adalah `{lo: 0, hi: 1}` dan bukan
 * pencarian ulang batas datanya, dan jendela tidak pernah bisa tersesat di
 * luar data yang melahirkannya. Sumbu penuhnya sendiri boleh berganti besaran
 * — dari RT ke RHOB — tanpa jendelanya ikut kehilangan arti.
 *
 * Semuanya murni dan tanpa React: yang dijawab di sini hanyalah di mana sebuah
 * angka berada di antara dua ujung, dan angka mana yang pantas ditulis di tepi.
 */

import { coordinateStep, coordinateTicks, logTicks, tickDecimals } from "./ticks";

/** Jendela yang tampak dari sebuah sumbu, sebagai pecahan 0..1 sumbu penuhnya. */
export interface Window {
  lo: number;
  hi: number;
}

/** Seluruh sumbu — yang dipulangkan tombol "kembali pas". */
export const WHOLE: Window = { lo: 0, hi: 1 };

/**
 * Jendela tersempit yang masih boleh diminta: 1/250 sumbu penuhnya.
 *
 * Batas ini bukan kerapian melainkan penjaga. Roda tetikus yang berputar
 * sepuluh kali tanpa lantai memulangkan jendela selebar 10⁻¹⁵ dari sumbunya,
 * dan pada lebar itu aritmetika pecahan biner sendiri yang menentukan di mana
 * sebuah titik tergambar.
 */
export const NARROWEST = 0.004;

/**
 * Sumbu penuh dalam ruang **terpetakan**.
 *
 * `lo` dan `hi` adalah logaritma nilainya bila `log`, dan nilainya sendiri
 * bila tidak. Disimpan begitu supaya zum, geser dan penempatan titik memakai
 * aritmetika yang sama untuk kedua macam sumbu: sebuah sumbu resistivitas yang
 * diperbesar di sekitar kursor adalah soal yang persis sama dengan sumbu
 * porositas, sesudah logaritmanya diambil sekali di sini.
 */
export interface Scale {
  lo: number;
  hi: number;
  log: boolean;
}

export interface Tick {
  value: number;
  /** Kedudukannya pada jendela yang tampak, 0 di pangkal dan 1 di ujung. */
  t: number;
  text: string;
}

const clamp = (value: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, value));

/**
 * Jendela sesudah dizum, berpusat pada `at` — pecahan sumbu **penuh**.
 *
 * Berpusat pada kursor dan bukan pada tengah jendela: yang diperbesar orang
 * adalah rombongan titik yang sedang ditunjuknya, dan zum yang melompat ke
 * tengah memaksa menggeser lagi sesudah tiap putaran roda.
 *
 * `factor` di bawah satu memperbesar. Hasilnya selalu di dalam 0..1 — sumbu
 * tidak bisa digeser keluar dari datanya sendiri, sebab di luar sana tidak ada
 * apa pun untuk dilihat dan yang tersesat di sana hanya bisa pulang lewat
 * tombol.
 */
export function zoomWindow(
  window: Window,
  at: number,
  factor: number,
  narrowest = NARROWEST,
): Window {
  const width = window.hi - window.lo;
  if (!(width > 0)) return WHOLE;
  const next = clamp(width * factor, Math.min(narrowest, width), 1);
  const lo = clamp(at - (at - window.lo) * (next / width), 0, 1 - next);
  return { lo, hi: lo + next };
}

/** Jendela sesudah digeser sejauh `delta` pecahan sumbu penuh. */
export function panWindow(window: Window, delta: number): Window {
  const width = window.hi - window.lo;
  if (!(width > 0)) return WHOLE;
  const lo = clamp(window.lo + delta, 0, 1 - width);
  return { lo, hi: lo + width };
}

/** Benar ketika jendelanya masih seluruh sumbu — tombol pulang lalu diredupkan. */
export function isWhole(window: Window): boolean {
  return window.lo <= 1e-9 && window.hi >= 1 - 1e-9;
}

/** Berapa kali lipat jendela ini memperbesar sumbu penuhnya. */
export function zoomOf(window: Window): number {
  const width = window.hi - window.lo;
  return width > 0 ? 1 / width : 1;
}

/** Bagian sumbu yang benar-benar tampak, sebagai sumbu tersendiri. */
export function windowScale(full: Scale, window: Window): Scale {
  const span = full.hi - full.lo;
  return {
    lo: full.lo + span * window.lo,
    hi: full.lo + span * window.hi,
    log: full.log,
  };
}

/**
 * Sumbu penuh dari sederet nilai, dibulatkan keluar ke angka yang bulat.
 *
 * Dibulatkan sebab tepi sumbu ikut dibaca: awan yang membentang 0,012 sampai
 * 0,283 mendapat sumbu 0,00–0,30, bukan sumbu yang ujungnya persis pada sampel
 * terbesar yang kebetulan ada. Pada sumbu logaritmik pembulatannya ke dekade
 * penuh, dengan alasan yang sama seperti `decadeBounds`: tanda sumbu lalu
 * jatuh di 0,1 · 1 · 10, angka yang langsung terbaca.
 *
 * Memulangkan `null` ketika tidak ada satu pun nilai yang bisa digambar.
 * Sumbu logaritmik menolak yang tidak positif, dan itu bukan pembersihan
 * melainkan kenyataan: log dari nol adalah minus tak hingga, dan sumbu yang
 * memuatnya tidak punya pangkal.
 */
export function scaleOf(
  values: readonly (number | null | undefined)[],
  log: boolean,
): Scale | null {
  const usable = values.filter(
    (v): v is number => typeof v === "number" && Number.isFinite(v) && (!log || v > 0),
  );
  if (!usable.length) return null;

  const min = Math.min(...usable);
  const max = Math.max(...usable);
  if (log) {
    const lo = Math.floor(Math.log10(min));
    const hi = Math.ceil(Math.log10(max));
    return { lo, hi: hi > lo ? hi : lo + 1, log };
  }
  const step = coordinateStep(max - min, 6);
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  return { lo, hi: hi > lo ? hi : lo + step, log };
}

/** Nilai dalam ruang terpetakan; NaN bila ia tidak punya tempat di sumbu ini. */
export function mapValue(scale: Scale, value: number): number {
  if (!Number.isFinite(value)) return NaN;
  if (!scale.log) return value;
  return value > 0 ? Math.log10(value) : NaN;
}

/**
 * Kedudukan sebuah nilai pada sumbunya, 0 di pangkal dan 1 di ujung.
 *
 * Boleh keluar dari 0..1 — itulah tepatnya yang membuat pemanggilnya bisa
 * membuang titik yang jatuh di luar jendela, dan yang dijepit ke tepi akan
 * berdiri di sana sebagai rombongan palsu.
 */
export function placeOn(scale: Scale, value: number): number {
  const mapped = mapValue(scale, value);
  const span = scale.hi - scale.lo;
  if (!Number.isFinite(mapped) || !(span > 0)) return NaN;
  return (mapped - scale.lo) / span;
}

/** Kebalikannya: nilai pada kedudukan `t`. Dipakai kursor dan pegangan seret. */
export function valueAt(scale: Scale, t: number): number {
  const mapped = scale.lo + (scale.hi - scale.lo) * t;
  return scale.log ? 10 ** mapped : mapped;
}

/**
 * Tanda sumbu untuk jendela yang sedang tampak, berikut tulisannya.
 *
 * Dihitung dari jendelanya dan bukan dari sumbu penuhnya, dan di situlah
 * seluruh gunanya: sumbu resistivitas yang diperbesar dari 0,1–1000 menjadi
 * 2–20 memulangkan tanda 2 · 5 · 10 · 20, bukan lima tanda dekade yang empat
 * di antaranya sudah berada di luar gambar.
 */
export function ticksOn(scale: Scale, divisions = 6): Tick[] {
  const span = scale.hi - scale.lo;
  if (!Number.isFinite(span) || span <= 0) return [];
  if (scale.log) {
    return logTicks(10 ** scale.lo, 10 ** scale.hi).map((value) => ({
      value,
      t: (Math.log10(value) - scale.lo) / span,
      text: value >= 1 ? value.toLocaleString("en-US") : String(value),
    }));
  }
  const step = coordinateStep(span, divisions);
  const decimals = tickDecimals(step);
  // Kelonggaran sepersejuta langkah di kedua ujung, dan ia menjawab satu
  // keadaan yang benar-benar terjadi: ujung sumbu di sini **dihitung dari
  // langkahnya sendiri**, jadi tanda teratas semestinya jatuh persis pada
  // ujungnya. Pecahan biner menaruhnya di 0,30000000000000004 sementara
  // ujungnya 0,3, dan yang dipulangkan adalah sumbu porositas yang berhenti
  // bertanda di 0,25 tanpa satu pun tanda kerusakan di permukaannya.
  const slack = step * 1e-6;
  return coordinateTicks(scale.lo - slack, scale.hi + slack, step).map((value) => ({
    value,
    t: (value - scale.lo) / span,
    text: value.toFixed(decimals),
  }));
}
